import { assign, fromPromise, setup } from 'xstate';
import { getData, postData } from '~/utils/service';

export interface VerifyResponse {
  employerName: string;
  status: string;
  invitationCode: string;
  invitedByName: string | null;
}

// [MCV-1804] Whether the signed-in provider is the one the invitation was sent to.
// recipientEmailHint is masked server-side and set only for a wrong-account result.
export interface RecipientResponse {
  isRecipient: boolean;
  requiresResend: boolean;
  recipientEmailHint: string | null;
  // True when the invitation went to an email that has no provider account yet
  // and the signed-in account is not that email. Resending cannot help; the user
  // has to sign in (or sign up) with the invited email. Absent from older servers.
  awaitsSignup?: boolean;
}

type Context = {
  code: string;
  verifyData: VerifyResponse | null;
  respondError: string | null;
  recipientEmailHint: string | null;
  awaitsSignup: boolean;
  // [MCV-1827] Set when an accept is refused by the single-employer rule.
  alreadyAssociated: AlreadyAssociated | null;
};

type Events = { type: 'ON_ACCEPT' } | { type: 'ON_DECLINE' };

const RESEND_REQUIRED_DETAIL =
  'Please ask the employer to resend this invitation before responding';

const requiresResend = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  // Only this known recovery message is safe to display. Other failures may
  // contain internal details and should retain the generic, retryable error.
  return response?.status === 400 && response.data?.detail === RESEND_REQUIRED_DETAIL;
};

// Must equal the detail the server sends in invitation_service.py.
export const WRONG_ACCOUNT_DETAIL =
  'Sign in with the provider account that received this invitation';

// The server refuses a response from any provider other than the invitee with
// this 403. Any other 403 is not a wrong-account result and stays a generic failure.
const isWrongAccount = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  return response?.status === 403 && response.data?.detail === WRONG_ACCOUNT_DETAIL;
};

// [MCV-1926] The server answers Accept/Decline on a 30-day-old pending invitation with
// 409 + extra.code INVITATION_EXPIRED. Its detail ("Ask the employer to send a new one")
// is safe to show; returns null for any other failure.
export const INVITATION_EXPIRED_CODE = 'INVITATION_EXPIRED';
const INVITATION_EXPIRED_MESSAGE =
  'This invitation has expired. Ask the employer to send a new one.';

const invitationExpiredDetail = (error: unknown): string | null => {
  const response = (
    error as {
      response?: { status?: number; data?: { detail?: unknown; extra?: { code?: unknown } } };
    }
  )?.response;
  if (response?.status !== 409 || response.data?.extra?.code !== INVITATION_EXPIRED_CODE) {
    return null;
  }
  const detail = response.data.detail;
  return typeof detail === 'string' && detail.trim() !== '' ? detail : INVITATION_EXPIRED_MESSAGE;
};

// [MCV-1827] Must equal the `extra.code` the server sends in invitation_service.py
// when a provider who already has an employer tries to accept another invitation.
export const ALREADY_ASSOCIATED_CODE = 'ALREADY_ASSOCIATED';

interface AlreadyAssociated {
  employerName: string | null;
  // True when the employer the provider already has is the one that sent this
  // invitation. Null from a server that does not say.
  sameEmployer: boolean | null;
}

const alreadyAssociated = (error: unknown): AlreadyAssociated | null => {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { extra?: { code?: unknown; employerName?: unknown; sameEmployer?: unknown } };
      };
    }
  )?.response;
  const extra = response?.data?.extra;
  if (response?.status !== 400 || extra?.code !== ALREADY_ASSOCIATED_CODE) return null;
  return {
    employerName: typeof extra.employerName === 'string' ? extra.employerName : null,
    sameEmployer: typeof extra.sameEmployer === 'boolean' ? extra.sameEmployer : null,
  };
};

export const verifyAssociationMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as { code: string },
  },
  actors: {
    verify: fromPromise(async ({ input }: { input: { code: string } }) => {
      const data = await getData<VerifyResponse>('providerAssociationVerify', {
        pathParams: { code: input.code },
      });
      return data;
    }),
    checkRecipient: fromPromise(async ({ input }: { input: { code: string } }) => {
      return getData<RecipientResponse>('providerAssociationRecipient', {
        pathParams: { code: input.code },
      });
    }),
    respond: fromPromise(async ({ input }: { input: { code: string; accept: boolean } }) => {
      await postData('providerAssociationAccept', {
        data: { invitation_code: input.code, accept: input.accept },
      });
      return input.accept;
    }),
  },
}).createMachine({
  id: 'verifyAssociation',
  context: ({ input }) => ({
    code: input.code,
    verifyData: null,
    respondError: null,
    recipientEmailHint: null,
    awaitsSignup: false,
    alreadyAssociated: null,
  }),
  initial: '$_VERIFYING',
  states: {
    $_VERIFYING: {
      invoke: {
        src: 'verify',
        input: ({ context }) => ({ code: context.code }),
        onDone: [
          {
            // [MCV-1804] A provider who signed up from an invitation email is
            // associated automatically at account creation, so the link they
            // return to is already ACCEPTED. Show the success, not "not found".
            guard: ({ event }) => event.output.status === 'ACCEPTED' && !!event.output.employerName,
            target: '$_ACCEPTED',
            actions: assign({ verifyData: ({ event }) => event.output }),
          },
          {
            // [MCV-1828] The notification outlives a decline, so the link it opens
            // lands here. Say so, rather than "not found".
            guard: ({ event }) => event.output.status === 'DECLINED' && !!event.output.employerName,
            target: '$_ALREADY_DECLINED',
            actions: assign({ verifyData: ({ event }) => event.output }),
          },
          {
            // [MCV-1926] Verify already reports a stale invitation as EXPIRED, so say so
            // up front rather than "not found" (the accept/decline 409 is the fallback).
            guard: ({ event }) => event.output.status === 'EXPIRED' && !!event.output.employerName,
            target: '$_EXPIRED',
            actions: assign({
              verifyData: ({ event }) => event.output,
              respondError: INVITATION_EXPIRED_MESSAGE,
            }),
          },
          {
            guard: ({ event }) => event.output.status !== 'PENDING' || !event.output.employerName,
            target: '$_INVALID',
          },
          {
            target: '$_CHECKING_RECIPIENT',
            actions: assign({ verifyData: ({ event }) => event.output }),
          },
        ],
        onError: '$_INVALID',
      },
    },

    // [MCV-1804] Ask who the invitation was sent to before offering Accept, so a
    // provider signed in as someone else is told so up front instead of hitting a
    // 403 on every click. A failed check never blocks: the server enforces on Accept.
    $_CHECKING_RECIPIENT: {
      invoke: {
        src: 'checkRecipient',
        input: ({ context }) => ({ code: context.code }),
        onDone: [
          {
            guard: ({ event }) => event.output.requiresResend,
            target: '$_RESEND_REQUIRED',
            actions: assign({ respondError: RESEND_REQUIRED_DETAIL }),
          },
          {
            guard: ({ event }) => !event.output.isRecipient,
            target: '$_WRONG_ACCOUNT',
            actions: assign({
              recipientEmailHint: ({ event }) => event.output.recipientEmailHint,
              awaitsSignup: ({ event }) => event.output.awaitsSignup === true,
            }),
          },
          { target: '$_SIGNED_IN' },
        ],
        onError: '$_SIGNED_IN',
      },
    },

    $_INVALID: {},

    $_SIGNED_IN: {
      on: {
        ON_ACCEPT: { target: '$_ACCEPTING', actions: assign({ respondError: null }) },
        ON_DECLINE: { target: '$_DECLINING', actions: assign({ respondError: null }) },
      },
    },

    $_ACCEPTING: {
      invoke: {
        src: 'respond',
        input: ({ context }) => ({ code: context.code, accept: true }),
        onDone: '$_ACCEPTED',
        onError: [
          {
            guard: ({ event }) => requiresResend(event.error),
            target: '$_RESEND_REQUIRED',
            actions: assign({ respondError: RESEND_REQUIRED_DETAIL }),
          },
          {
            // [MCV-1926] Expired: say so and block further clicks, like a resend-required invitation.
            guard: ({ event }) => invitationExpiredDetail(event.error) !== null,
            target: '$_EXPIRED',
            actions: assign({ respondError: ({ event }) => invitationExpiredDetail(event.error) }),
          },
          {
            // [MCV-1804] Wrong provider account: show the sign-out card, not the
            // generic toast, so the invitee is not left retrying a refused click.
            guard: ({ event }) => isWrongAccount(event.error),
            target: '$_WRONG_ACCOUNT',
          },
          {
            // [MCV-1827] Already associated with an employer: explain which one and
            // what to do, instead of the generic toast.
            guard: ({ event }) => alreadyAssociated(event.error) !== null,
            target: '$_ALREADY_ASSOCIATED',
            actions: assign({ alreadyAssociated: ({ event }) => alreadyAssociated(event.error) }),
          },
          {
            target: '$_SIGNED_IN',
            actions: assign({ respondError: 'Failed to process invitation' }),
          },
        ],
      },
    },

    $_DECLINING: {
      invoke: {
        src: 'respond',
        input: ({ context }) => ({ code: context.code, accept: false }),
        onDone: '$_DECLINED',
        onError: [
          {
            guard: ({ event }) => requiresResend(event.error),
            target: '$_RESEND_REQUIRED',
            actions: assign({ respondError: RESEND_REQUIRED_DETAIL }),
          },
          {
            // [MCV-1926] Expired: say so and block further clicks, like a resend-required invitation.
            guard: ({ event }) => invitationExpiredDetail(event.error) !== null,
            target: '$_EXPIRED',
            actions: assign({ respondError: ({ event }) => invitationExpiredDetail(event.error) }),
          },
          {
            // [MCV-1804] Wrong provider account: show the sign-out card, not the
            // generic toast, so the invitee is not left retrying a refused click.
            guard: ({ event }) => isWrongAccount(event.error),
            target: '$_WRONG_ACCOUNT',
          },
          {
            target: '$_SIGNED_IN',
            actions: assign({ respondError: 'Failed to process invitation' }),
          },
        ],
      },
    },

    $_WRONG_ACCOUNT: {},
    $_ACCEPTED: {},
    $_DECLINED: {},
    $_ALREADY_DECLINED: {},
    $_ALREADY_ASSOCIATED: {},
    $_RESEND_REQUIRED: {},
    $_EXPIRED: {},
  },
});
